import { useEffect, useMemo, useRef, useState } from 'react';
import { userAppContext } from '@/context/Context';
import type { EvidenceDistributionMetrics } from '@/utils/evidenceDistributionMetrics';
import { fetchEvidenceDistributionMetrics } from '@/utils/fetchEvidenceDistribution';
import type { AnalyticsTabKey } from './analyticsConfig';

const DEBOUNCE_MS = 150;

type UseSearchEvidenceSampleParams = {
  source: string;
  view: string;
  associationTab: AnalyticsTabKey;
  searchItems?: Array<{ id: string }>;
  enabled?: boolean;
};

export function useSearchEvidenceSample({
  source,
  view,
  associationTab,
  searchItems,
  enabled = true,
}: UseSearchEvidenceSampleParams) {
  const [metrics, setMetrics] = useState<EvidenceDistributionMetrics | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const requestIdRef = useRef(0);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { authorization, setIsAuthenticated, setAuthError } = (userAppContext() || {}) as {
    authorization?: unknown;
    setIsAuthenticated?: (value: boolean) => void;
    setAuthError?: (error: unknown) => void;
  };

  const searchItemsKey = searchItems?.map(i => String(i?.id ?? '')).join(',') ?? '';
  const isEligible =
    enabled && (associationTab === 'GDA' || associationTab === 'VDA') && searchItemsKey !== '';

  const triggerKey = useMemo(
    () =>
      JSON.stringify({
        source,
        view,
        associationTab,
        searchItemsKey,
        isEligible,
      }),
    [associationTab, isEligible, searchItemsKey, source, view],
  );

  const paramsRef = useRef({
    source,
    view,
    associationTab,
    searchItems,
    authorization,
    setIsAuthenticated,
    setAuthError,
    isEligible,
  });

  paramsRef.current = {
    source,
    view,
    associationTab,
    searchItems,
    authorization,
    setIsAuthenticated,
    setAuthError,
    isEligible,
  };

  useEffect(() => {
    const p = paramsRef.current;

    if (!p.isEligible) {
      requestIdRef.current += 1;
      setMetrics(null);
      setIsLoading(false);
      setError(null);
      return;
    }

    const requestId = ++requestIdRef.current;
    let cancelled = false;

    setIsLoading(true);
    setError(null);

    const loadData = async () => {
      try {
        const next = await fetchEvidenceDistributionMetrics({
          scope: {
            view: p.view,
            source: p.source,
            tab: p.associationTab,
            searchItems: p.searchItems ?? [],
            isMapping: false,
            mappingContent: null,
          },
          auth: {
            authorization: p.authorization,
            setAuthError: p.setAuthError ?? p.setIsAuthenticated,
            isAuthenticated: Boolean(p.authorization),
          },
        });

        if (cancelled || requestId !== requestIdRef.current) return;
        setMetrics(next);
      } catch (fetchErr: unknown) {
        if (cancelled || requestId !== requestIdRef.current) return;
        setError(fetchErr instanceof Error ? fetchErr.message : 'Error fetching evidence distribution');
        setMetrics(null);
      } finally {
        if (!cancelled && requestId === requestIdRef.current) {
          setIsLoading(false);
        }
      }
    };

    debounceRef.current = setTimeout(() => {
      void loadData();
    }, DEBOUNCE_MS);

    return () => {
      cancelled = true;
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
      requestIdRef.current += 1;
    };
  }, [triggerKey]);

  return {
    metrics,
    totalEvidenceCount: metrics?.totalRecords ?? null,
    isLoading,
    error,
  };
}
